import { Box, Stack, TextField } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import { TypeHelper } from "../../helpers/type";
import {
  TabularFilterProps,
  TabularQuickFilterProps,
  TabularRefetchProps,
  TabularSortProps,
} from "../../ts/dataTabular";
import AppBadge from "../Badge/AppBadge";
import AppIconButton from "../Button/AppIconButton";
import FilterPopover, { FilterPopoverHandle } from "../Filters/FilterPopover";
import AppIcon from "../Icons/AppIcon";
import SortPopover from "../Sort/SortPopover";
import AppTooltip from "../Tooltip/AppTooltip";
import { ToolbarIconButton } from "./ToolbarIconButton";

interface Props {
  // sort?: {
  //   sortSpec?: SortSpec;
  //   sortDefinition: SortDefinition | undefined;
  //   maxSortItems?: number;
  //   onChange: (newValue: SortDefinition | undefined) => void;
  // };
  // quickFilter?: {
  //   initialSearch: string | undefined;
  //   search: string | undefined;
  //   onChange: (newValue: string | undefined) => void;
  // };
  // filters?: {
  //   filterSpec: FilterSpec | undefined;
  //   filterDefinition: FilterDefinition | undefined;
  //   onChange: (newValue: FilterDefinition | undefined) => void;
  // };
  sort?: TabularSortProps;
  quickFilter?: TabularQuickFilterProps;
  filters?: TabularFilterProps;
  refetch?: TabularRefetchProps;
}

/** Similar to CustomDataGridToolbar but for custom paginated lists. */
export default function PaginatedListToolbar({ sort, quickFilter, filters, refetch }: Props) {
  const filterPopoverRef = useRef<FilterPopoverHandle>(null);

  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const [search, setSearch] = useState<string | undefined>(quickFilter?.initialSearch);
  const [isRefetching, setIsRefetching] = useState(false);

  // const isTabsEnabled = useMemo(() => !!tabs, [tabs]);
  const isSortEnabled = useMemo(() => !!sort, [sort]);
  const isQuickFilterEnabled = useMemo(() => !!quickFilter, [quickFilter]);
  const isFiltersEnabled = useMemo(() => !!filters, [filters]);
  const isRefetchEnabled = useMemo(() => !!refetch, [refetch]);
  const enabledList = [
    // isTabsEnabled,
    isSortEnabled,
    isQuickFilterEnabled,
    isFiltersEnabled,
    isRefetchEnabled,
  ];
  const isAnyEnabled = useMemo(() => enabledList.some((x) => x === true), [...enabledList]);

  // refetch integration
  useEffect(() => {
    const sub1 = refetch?.eventEmitter.on2("refetchStarted", () => {
      setIsRefetching(true);
    });
    const sub2 = refetch?.eventEmitter.on2("refetchEnded", () => {
      setIsRefetching(false);
    });
    return () => {
      sub1?.off();
      sub2?.off();
    };
  }, [refetch]);

  if (!isAnyEnabled) {
    return null;
  }

  return (
    <Box
      data-testid='PaginatedListToolbar'
      sx={{
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gap: 2,
          // gridTemplateColumns: { xxs: "1fr", lg: "1fr 1fr" },
          gridTemplateColumns: { xxs: "1fr", lg: "1fr" },
        }}
      >
        {/* TODO: check DataGridToolbar tabs before enabling */}
        {/* <Box> */}
        {/* Tabs */}
        {/* {isTabsEnabled && tabs && (
              <AppTabs value={tabs.value} onChange={tabs.onChange}>
                {tabs.tabs.map((tab, index) => (
                  <AppTab key={index} {...tab} />
                ))}
              </AppTabs>
            )} */}
        {/* </Box> */}

        {/* Actions */}
        <Stack
          direction='row'
          spacing={0.25}
          sx={{ justifyContent: "flex-end", alignItems: "center", pr: 1 }}
        >
          {/* Quick filter */}
          {isQuickFilterEnabled && quickFilter && !isSearchOpened && (
            <AppTooltip title='Quick search' isDefaultEnterDelays>
              <AppBadge
                variant='dot'
                color='secondary'
                overlap='circular'
                invisible={TypeHelper.isEmpty(search)}
              >
                <ToolbarIconButton onClick={() => setIsSearchOpened(true)}>
                  <AppIcon of='search' />
                </ToolbarIconButton>
              </AppBadge>
            </AppTooltip>
          )}
          {isQuickFilterEnabled && quickFilter && isSearchOpened && (
            <TextField
              sx={{ flex: 1, "& .MuiInputBase-root": { pr: 0 }, minWidth: 0 }}
              label='Search'
              size='small'
              value={search || ""}
              onChange={(e) => {
                setSearch(e.target.value);
                quickFilter.onChange(e.target.value || undefined);
              }}
              autoFocus
              slotProps={{
                input: {
                  endAdornment: (
                    <AppIconButton
                      onClick={() => {
                        setIsSearchOpened(false);
                        setSearch(undefined);
                        quickFilter.onChange(undefined);
                      }}
                    >
                      <AppIcon of='close' />
                    </AppIconButton>
                  ),
                },
              }}
            />
          )}

          {isFiltersEnabled && filters && filters.filterSpec && (
            <FilterPopover
              ref={filterPopoverRef}
              clickBehavior={{}}
              trigger={
                <AppTooltip title='Filters' isDefaultEnterDelays>
                  <AppBadge
                    color='secondary'
                    overlap='circular'
                    badgeContent={filters.filterDefinition?.items?.length}
                  >
                    <ToolbarIconButton>
                      <AppIcon of='filters' />
                    </ToolbarIconButton>
                  </AppBadge>
                </AppTooltip>
              }
              filterDefinitionInputProps={{
                filterSpec: filters.filterSpec,
                filterDefinition: filters.filterDefinition,
                onChange: filters.onChange,
              }}
            />
          )}

          {isSortEnabled && sort && sort.sortSpec && (
            <SortPopover
              clickBehavior={{}}
              trigger={
                <AppTooltip title='Sort' isDefaultEnterDelays>
                  <AppBadge
                    color='secondary'
                    overlap='circular'
                    badgeContent={sort.sortDefinition?.items?.length}
                  >
                    <ToolbarIconButton>
                      <AppIcon of='sorting' />
                    </ToolbarIconButton>
                  </AppBadge>
                </AppTooltip>
              }
              sortDefinitionInputProps={{
                sortSpec: sort.sortSpec,
                sortDefinition: sort.sortDefinition,
                maxSortItems: sort.maxSortItems,
                onChange: sort.onChange,
              }}
            />
          )}

          {/* Refetch */}
          {isRefetchEnabled && refetch && (
            <AppTooltip title='Refresh' isDefaultEnterDelays>
              <ToolbarIconButton
                disabled={isRefetching}
                onClick={() => {
                  refetch.triggerRefetch();
                }}
              >
                <AppIcon of='refresh' />
              </ToolbarIconButton>
            </AppTooltip>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
